import { useContext, useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@App/contexts/Application';
import { ApplicationStage } from '@App/interfaces/application-stage.enum';
import { AuthContext } from '@src/app/contexts/Auth';

import { LoaderProgress } from '../LoaderProgress';

const ProtectedEligibility = (props: { children: any }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [waiting, setWaiting] = useState(true);
    const fallback = '/eligibility';

    const {
        dashboardItemStatuses,
        applicationStage,
        callDateTime,
        debtConUser,
    } = useContext(ApplicationContext);

    const { loggedIn, isToFetchApplicationData } = useContext(AuthContext);

    useEffect(() => {
        const isLocationAllowed = (path: string) => {
            if (!path.includes('eligibility')) return true;

            const stage = path.split('/').reverse()[0];
            const getCompletedStatus = (stage: string) =>
                dashboardItemStatuses.find((item: any) => item.name === stage)
                    ?.completed;

            const isAfterDecisioning =
                applicationStage === ApplicationStage.DECISIONING_DECLINE ||
                applicationStage === ApplicationStage.CLOSED ||
                (applicationStage === ApplicationStage.DECISIONING_ACCEPT &&
                    isToFetchApplicationData) ||
                applicationStage === ApplicationStage.DECISIONING_ERROR;

            const stageDependencies: Record<string, string[]> = {
                'loan-details': [],
                applicants: ['loan-details'],
                applicants2: ['loan-details'],
                'property-details': ['applicants'],
                income: ['property-details'],
                income2: ['property-details'],
                // TODO: income is true on inital load but it should be false
                outgoings: ['income', 'property-details'],
                wait: ['outgoings'],
                intro: ['outgoings'],
                'main-goal': ['outgoings'],
                borrow: ['outgoings'],
                transition: ['outgoings'],
                input: ['outgoings'],
                'new-input': ['outgoings'],
                review: ['outgoings'],
                approval: ['debt-con'],
                finalise: ['quote'],
                'htb-next-steps': ['quote'],
                'book-call': ['quote'],
                confirmation: ['quote'],
            };

            if (!stage) return false;

            // cant access these pages after decisioning
            const stagesWithDecisioningCheck = new Set([
                'loan-details',
                'applicants',
                'applicants2',
                'property-details',
                'income',
                'income2',
                'outgoings',
                'intro',
                'main-goal',
                'borrow',
                'transition',
                'input',
                'new-input',
                'review',
            ]);

            const stagesWithoutDecisioningCheck = new Set([
                'wait',
                'approval',
                'finalise',
                'htb-next-steps',
                'book-call',
                'confirmation',
            ]);

            if (stage === 'loan-details') {
                return !isAfterDecisioning;
            }

            if (stage === 'explore') {
                return getCompletedStatus('quote') && !callDateTime;
            }

            const additionalStageInfo = path.split('/').reverse()[1];

            if (
                stage === 'homepage' ||
                stage === 'questions' ||
                (stage === 'book-call' &&
                    additionalStageInfo === 'loan-preferences') ||
                (stage === 'confirmation' &&
                    additionalStageInfo === 'book-call') ||
                stage === 'calendar'
            ) {
                return (
                    getCompletedStatus('outgoings') &&
                    !getCompletedStatus('quote')
                );
            }

            if (stagesWithoutDecisioningCheck.has(stage)) {
                return (
                    stageDependencies[stage]?.every(getCompletedStatus) ?? false
                );
            }

            if (stagesWithDecisioningCheck.has(stage)) {
                const isDependenciesComplete =
                    stageDependencies[stage]?.every(getCompletedStatus) ??
                    false;

                if (stage === 'outgoings') {
                    return !isAfterDecisioning && isDependenciesComplete;
                }

                return !isAfterDecisioning && isDependenciesComplete;
            }

            return false;
        };

        const isAllowed = isLocationAllowed(location.pathname);
        if (isAllowed) setWaiting(false);
        if (!isAllowed) {
            if (!loggedIn) {
                const path = '/eligibility/applicants';
                navigate(path, { replace: true });
            } else {
                navigate(fallback, { replace: true });
            }
        }
    }, [
        applicationStage,
        dashboardItemStatuses,
        location.pathname,
        navigate,
        loggedIn,
        isToFetchApplicationData,
        callDateTime,
        debtConUser,
    ]);

    return waiting ? <LoaderProgress fullScreen /> : <>{props.children}</>;
};

export default ProtectedEligibility;
